import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem as PrimeMenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { FEATURES } from '@/app/shared/constants/features';
import { PERMISSIONS } from '@/app/shared/constants/permissions';
import { UserStateService } from '@/app/features/user/services';
import { FeatureFlagService } from '@/app/core/services/feature-flag/feature-flag.service';

export interface MenuItem extends PrimeMenuItem {
  path?: string;
  items?: MenuItem[];
  permission?: string | string[];
  featureKey?: string;
  /** Visible only to the platform billing admin (userData().isAdmin) - see AdminEmailGate. */
  adminOnly?: boolean;
}

@Component({
  selector: '[app-menu]',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `<ul class="layout-menu">
    @for (item of visibleMenu(); track $index) {
      @if (!item.separator) {
        <li app-menuitem [item]="item" [root]="true"></li>
      } @else {
        <li class="menu-separator"></li>
      }
    }
  </ul>`,
})
export class AppMenu {
  private readonly userStateService = inject(UserStateService);
  private readonly featureFlagService = inject(FeatureFlagService);

  private readonly baseModel: MenuItem[] = [
    {
      label: 'Principal',
      icon: 'pi pi-home',
      path: '/dashboard',
      items: [
        {
          label: 'Dashboard',
          icon: 'pi pi-home',
          routerLink: ['/dashboard'],
        },
      ],
    },
    { separator: true },
    {
      label: 'Usuarios',
      icon: 'pi pi-users',
      path: '/admin',
      permission: [PERMISSIONS.Users.Read, PERMISSIONS.Roles.Read],
      items: [
        {
          label: 'Lista de Usuarios',
          icon: 'pi pi-list',
          routerLink: ['/admin/user-list'],
          permission: PERMISSIONS.Users.Read,
        },
        {
          label: 'Roles y Permisos',
          icon: 'pi pi-id-card',
          routerLink: ['/admin/role-list'],
          permission: PERMISSIONS.Roles.Read,
        },
        {
          label: 'Vendedores',
          icon: 'pi pi-briefcase',
          routerLink: ['/admin/vendedor-list'],
          permission: PERMISSIONS.Users.Read,
        },
      ],
    },
    { separator: true },
    {
      label: 'Órdenes',
      icon: 'pi pi-folder-open',
      path: '/order',
      permission: PERMISSIONS.Orders.Read,
      items: [
        {
          label: 'Órdenes',
          icon: 'pi pi-list',
          routerLink: ['/order'],
          permission: [PERMISSIONS.Orders.Read],
        },
        {
          label: 'Plantillas',
          icon: 'pi pi-file',
          routerLink: ['/template'],
          permission: PERMISSIONS.Templates.Read,
        },
        {
          label: 'Campos de Orden',
          icon: 'pi pi-table',
          routerLink: ['/field'],
          permission: PERMISSIONS.Fields.Read,
        },
      ],
    },
    { separator: true },
    {
      label: 'Gestión',
      icon: 'pi pi-building',
      path: '/gestion',
      items: [
        {
          label: 'Clientes',
          icon: 'pi pi-building',
          routerLink: ['/customer'],
          permission: [PERMISSIONS.Customers.Read],
          featureKey: FEATURES.Customers,
        },
        {
          label: 'Líneas de Producción',
          icon: 'pi pi-sitemap',
          routerLink: ['/production-line'],
          permission: PERMISSIONS.ProductionLines.Read,
          featureKey: FEATURES.ProductionLines,
        },
        {
          label: 'Inventario',
          icon: 'pi pi-box',
          routerLink: ['/inventory'],
          permission: PERMISSIONS.Inventory.Read,
          featureKey: FEATURES.Inventory,
        },
      ],
    },
    { separator: true },
    {
      label: 'Sistema',
      icon: 'pi pi-cog',
      path: '/settings',
      permission: [
        PERMISSIONS.PermissionsCatalog.Read,
        PERMISSIONS.Integrations.ManageSiigoCredentials,
      ],
      items: [
        {
          label: 'Configuración',
          icon: 'pi pi-cog',
          routerLink: ['/settings'],
          permission: [
            PERMISSIONS.PermissionsCatalog.Read,
            PERMISSIONS.Integrations.ManageSiigoCredentials,
          ],
        },
      ],
    },
    { separator: true },
    {
      label: 'Plataforma',
      icon: 'pi pi-shield',
      path: '/admin/billing',
      adminOnly: true,
      items: [
        {
          label: 'Suscripciones de clientes',
          icon: 'pi pi-wallet',
          routerLink: ['/admin/billing'],
          adminOnly: true,
        },
      ],
    },
  ];

  visibleMenu = computed(() => {
    const userData = this.userStateService.userData();
    if (!userData?.id) return [];
    const permissions = new Set(userData.permissions);
    const features = this.featureFlagService.features();
    return this.filterMenu(
      this.baseModel,
      permissions,
      features,
      userData.isAdmin,
    );
  });

  private filterMenu(
    items: MenuItem[],
    permissions: Set<string>,
    features: { featureKey: string; isEnabled: boolean }[],
    isAdmin: boolean,
  ): MenuItem[] {
    const result: MenuItem[] = [];
    for (const item of items) {
      if (item.separator) {
        result.push(item);
        continue;
      }
      if (item.adminOnly && !isAdmin) continue;
      if (item.permission) {
        const required = Array.isArray(item.permission)
          ? item.permission
          : [item.permission];
        if (!required.some((p) => permissions.has(p))) continue;
      }
      if (item.featureKey) {
        const feat = features.find((f) => f.featureKey === item.featureKey);
        if (feat && !feat.isEnabled) continue;
      }
      const entry: MenuItem = { ...item };
      if (item.items?.length) {
        entry.items = this.filterMenu(
          item.items,
          permissions,
          features,
          isAdmin,
        );
        if (!entry.items.some((i) => !i.separator)) continue;
      }
      result.push(entry);
    }
    return result.filter(
      (item, i, arr) =>
        !item.separator ||
        (i > 0 &&
          i < arr.length - 1 &&
          !arr[i - 1].separator &&
          !arr[i + 1]?.separator),
    );
  }
}
